// Adapted from eval/dashboard/internal_web/src/components/session/coordinateTimeline.ts (ATM-57): relationWords only. Keep in step until ATM-60 shares it.
/**
 * A knowledge-graph relation in a doctor's words, as the eval dashboard labels them
 * (`coordinateTimeline.relationWords`): `pain_has_character` → "pain · character",
 * `has_non_pain_symptom` → "symptom", `associated_with_medical_history` → "medical
 * history". A relation outside those shapes keeps its own words, so a new graph relation
 * still lands somewhere readable.
 */

import { snakeWords } from "./format";

type Subject = "pain" | "non_pain";

function relationParts(relation: string): { subject: Subject | null; facet: string } {
  const symptom = /^has_(non_pain|pain)_symptom$/.exec(relation);
  if (symptom !== null) return { subject: symptom[1] as Subject, facet: "symptom" };
  const scoped = /^(non_pain|pain)_(.+)$/.exec(relation);
  if (scoped !== null) {
    return { subject: scoped[1] as Subject, facet: snakeWords(scoped[2].replace(/^has_/, "")) };
  }
  const associated = /^associated_with_(.+)$/.exec(relation);
  if (associated !== null) return { subject: null, facet: snakeWords(associated[1]) };
  return { subject: null, facet: snakeWords(relation.replace(/^has_/, "")) };
}

/** The relation in a few words: "pain · character", "symptom · onset speed", "medical history". */
export function relationWords(relation: string): string {
  const { subject, facet } = relationParts(relation);
  if (subject === null) return facet;
  const symptom = subject === "pain" ? "pain" : "symptom";
  return facet === "symptom"
    ? subject === "pain"
      ? "pain symptom"
      : "symptom"
    : `${symptom} · ${facet}`;
}
